/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { useEffect, useState } from 'react'

// react-router-dom components
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import { NavLink, useLocation } from 'react-router-dom'

// @mui material components

// Material Dashboard 2 PRO React TS components
import laurelLogo from 'assets/images/laurel-logo.png'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { UserRole } from 'context/auth/types'
import {
	setMiniSidenav,
	setTransparentSidenav,
	setWhiteSidenav,
	useMaterialUIController,
} from 'context/materialUI'
import SidenavCollapse from 'modules/Sidenav/SidenavCollapse'
import SidenavItem from 'modules/Sidenav/SidenavItem'
import SidenavList from 'modules/Sidenav/SidenavList'
import SidenavRoot from 'modules/Sidenav/SidenavRoot'
import { RouteType } from 'routes/types'

// Material Dashboard 2 PRO React context

// Declaring props types for Sidenav
interface Props {
	color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'dark'
	routes: RouteType[]
	user: {
		email: string
		role: UserRole
		name: string
	}
	[key: string]: any
}

function Sidenav({ color, routes, user, ...rest }: Props): JSX.Element {
	const [openCollapse, setOpenCollapse] = useState<boolean | string>(false)
	const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false)
	const [controller, dispatch] = useMaterialUIController()
	const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, orgAssets } = controller
	const location = useLocation()
	const { pathname } = location
	const collapseName = pathname.split('/').slice(1)[0]
	const items = pathname.split('/').slice(1)
	const itemParentName = items[1]
	const itemName = items[items.length - 1]

	let textColor:
		| 'primary'
		| 'secondary'
		| 'info'
		| 'success'
		| 'warning'
		| 'error'
		| 'dark'
		| 'white'
		| 'inherit'
		| 'text'
		| 'light' = 'white'

	if (transparentSidenav || (whiteSidenav && !darkMode)) {
		textColor = 'dark'
	} else if (whiteSidenav && darkMode) {
		textColor = 'inherit'
	}

	const closeSidenav = () => setMiniSidenav(dispatch, true)

	useEffect(() => {
		setOpenCollapse(collapseName)
		setOpenNestedCollapse(itemParentName)
	}, [collapseName, itemParentName])

	useEffect(() => {
		// A function that sets the mini state of the sidenav.
		function handleMiniSidenav() {
			setMiniSidenav(dispatch, window.innerWidth < 1200)
			setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav)
			setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav)
		}

		window.addEventListener('resize', handleMiniSidenav)

		handleMiniSidenav()

		return () => window.removeEventListener('resize', handleMiniSidenav)
	}, [dispatch, location, transparentSidenav, whiteSidenav])

	// Render all the nested collapse items from the routes.js
	const renderNestedCollapse = (collapse: any) => {
		const template = collapse.map(({ name, route, key, href }: any) =>
			href ? (
				<Link
					key={key}
					href={href}
					target="_blank"
					rel="noreferrer"
					sx={{ textDecoration: 'none' }}
				>
					<SidenavItem name={name} nested />
				</Link>
			) : (
				<NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
					<SidenavItem name={name} active={route === pathname} nested />
				</NavLink>
			)
		)

		return template
	}
	// Render the all the collpases from the routes.js
	const renderCollapse = (collapses: any) =>
		collapses.map(({ name, collapse, route, href, key }: any) => {
			let returnValue

			if (collapse) {
				returnValue = (
					<SidenavItem
						key={key}
						color={color}
						name={name}
						active={key === itemParentName ? 'isParent' : false}
						open={openNestedCollapse === key}
						onClick={({ currentTarget }: any) =>
							openNestedCollapse === key && currentTarget.classList.contains('MuiListItem-root')
								? setOpenNestedCollapse(false)
								: setOpenNestedCollapse(key)
						}
					>
						{renderNestedCollapse(collapse)}
					</SidenavItem>
				)
			} else {
				returnValue = href ? (
					<Link
						href={href}
						key={key}
						target="_blank"
						rel="noreferrer"
						sx={{ textDecoration: 'none' }}
					>
						<SidenavItem color={color} name={name} active={key === itemName} />
					</Link>
				) : (
					<NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
						<SidenavItem color={color} name={name} active={key === itemName} />
					</NavLink>
				)
			}
			return <SidenavList key={key}>{returnValue}</SidenavList>
		})

	// Render all the routes from the routes
	const renderRoutes = routes.map(
		({ type, name, icon, title, collapse, noCollapse, key, href, route, roleRequired }) => {
			let returnValue
			if (roleRequired && roleRequired.every(r => r !== user.role)) {
				return null
			}
			if (type === 'collapse') {
				if (href) {
					returnValue = (
						<Link
							href={href}
							key={key}
							target="_blank"
							rel="noreferrer"
							sx={{ textDecoration: 'none' }}
						>
							<SidenavCollapse
								name={name}
								icon={icon}
								active={key === collapseName}
								noCollapse={noCollapse}
							/>
						</Link>
					)
				} else if (noCollapse && route) {
					returnValue = (
						<NavLink to={route} key={key}>
							<SidenavCollapse
								name={name === `\${email}` ? user.name || user.email : name}
								icon={icon}
								noCollapse={noCollapse}
								active={key === collapseName}
							>
								{collapse ? renderCollapse(collapse) : null}
							</SidenavCollapse>
						</NavLink>
					)
				} else {
					returnValue = (
						<SidenavCollapse
							key={key}
							name={name}
							icon={icon}
							active={key === collapseName}
							open={openCollapse === key}
							onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
						>
							{collapse ? renderCollapse(collapse) : null}
						</SidenavCollapse>
					)
				}
			} else if (type === 'title') {
				if (!miniSidenav) {
					returnValue = (
						<MDTypography
							key={key}
							color={textColor}
							display="block"
							variant="caption"
							fontWeight="bold"
							textTransform="uppercase"
							pl={3}
							mt={2}
							mb={1}
							ml={1}
						>
							{title}
						</MDTypography>
					)
				}
			} else if (type === 'divider') {
				returnValue = (
					<Divider
						key={key}
						light={
							(!darkMode && !whiteSidenav && !transparentSidenav) ||
							(darkMode && !transparentSidenav && whiteSidenav)
						}
					/>
				)
			}

			return returnValue
		}
	)

	return (
		<SidenavRoot
			{...rest}
			variant="permanent"
			ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
		>
			<MDBox pt={3} pb={1} px={4} textAlign="center">
				<MDBox
					display={{ xs: 'block', xl: 'none' }}
					position="absolute"
					top={0}
					right={0}
					p={1.625}
					onClick={closeSidenav}
					sx={{ cursor: 'pointer' }}
				>
					<MDTypography variant="h6" color="secondary">
						<Icon sx={{ fontWeight: 'bold' }}>close</Icon>
					</MDTypography>
				</MDBox>
				<MDBox display="flex" alignItems="center" justifyContent={'center'}>
					<MDBox
						component="img"
						src={orgAssets?.logo?.dark || orgAssets?.logo?.light || laurelLogo}
						alt="Brand"
						width="90%"
					/>
				</MDBox>
			</MDBox>
			<Divider
				light={
					(!darkMode && !whiteSidenav && !transparentSidenav) ||
					(darkMode && !transparentSidenav && whiteSidenav)
				}
			/>
			<List>{renderRoutes}</List>
		</SidenavRoot>
	)
}

// Declaring default props for Sidenav
Sidenav.defaultProps = {
	color: 'info',
	brand: '',
}

export default Sidenav
